namespace $ {
	export class $shm_hitalama_guide_content_recipes extends $mol_object {
		static pages(): Record<string, $shm_hitalama_guide_content_page> {
			const page = this.$.$shm_hitalama_guide_content_page
			return {
				'maps-recipes': page('Рецепты работы с картой', 'folder-map-recipes', `Рецепт — небольшая законченная задача с понятным результатом. Начните на отдельной приватной карте с тестовыми данными. Когда всё работает, переносите подход в рабочее пространство и выдавайте доступ участникам.

## Задачи для первого знакомства

- **[CRM: клиенты и задачи](maps-recipe-crm.md)** — два набора сущностей, связь задачи с клиентом и обратный список задач. Подходит для заявок, проектов и сопровождения клиентов.
- **[Отчёт с графиком и фильтром](maps-recipe-dashboard.md)** — заменить данные готового пресета, настроить ось и показатель, проверить фильтр и собрать обзорную панель.
- **[Короткая презентация](maps-presentation.md)** — разложить материал по пронумерованным рамкам и пройти последовательность в режиме просмотра.

## Ещё несколько сочетаний

**Рабочая инструкция:** добавьте Markdown с назначением и шагами, рядом — изображение или пример формы. Полезные виджеты закрепите в быстром доступе, чтобы открывать их при работе в других разделах.

**Интерактивный инструмент:** возьмите готовое приложение из меню карты, разместите экземпляр рядом с данными и пояснением. Если нужен свой интерфейс, начните с небольшого модуля и проверьте реальный запуск без демо-заглушек.

**Совместный разбор:** создайте отдельные заметки для разных участников, объедините их контейнером, выдайте запись только нужным людям. Так меньше риск одновременной перезаписи одного общего текста.

## Как понять, что рецепт готов

Повторите действия без помощи автора, проверьте ошибочный и пустой ввод, затем откройте карту вторым клиентом. Убедитесь, что видны не только подписи, но и записи, связанные карточки и вложения. Сохраните резервную копию перед дальнейшим расширением.

[Все возможности карты](maps.md) · [Быстрый доступ и контейнеры](maps-layout.md)`),
				'maps-recipe-crm': page('Рецепт: клиенты и задачи', 'folder-map-recipes', `**Результат:** у каждой задачи выбран клиент, а из клиента можно открыть его задачи. Сначала сделайте 2 клиента и 3 задачи — этого достаточно для проверки структуры.

## 1. Подготовить два набора

На новой приватной карте добавьте **Таблица сущностей** дважды. Каждое добавление создаёт связанную тройку: редактор схемы, форма и таблица. Дайте наборам и виджетам различимые названия: Клиенты и Задачи.

В схеме клиентов оставьте обязательное Название, добавьте Контакт типа Строка и Заметку типа Многострочный текст. В схеме задач — обязательное Название, Срок типа Дата и Статус типа Выбор. В меню столбца Статус задайте значения Новая, В работе, Готово по одному на строке.

## 2. Связать задачу с клиентом

В таблицу задач добавьте столбец Клиент. В его меню выберите тип **Связь**, связанную таблицу Клиенты и отображаемый столбец Название.

Для просмотра с другой стороны создайте у клиентов столбец Задачи типа **Список связей**, направленный на задачи, и выберите его как обратный столбец поля Клиент. Если интерфейс предлагает **Создать обратный столбец**, используйте его вместо ручного создания второго независимого списка.

Для удобной визуальной настройки откройте схему через **Создать схему сущности** в ПКМ-меню таблицы. У свойства доступны связанная сущность и обратное свойство.

## 3. Ввести тестовые записи

Создайте двух клиентов через формы. Добавьте три задачи и выбирайте клиента через меню связи, а не вводите его название обычной строкой. Откройте карточку клиента из ячейки задачи и проверьте его обратный список.

Для исполнителей позже можно создать третий набор Участники и добавить связь с ним. Не используйте это расширение до проверки основной пары Клиенты — Задачи.

## 4. Проверить перед реальной работой

- Переименуйте клиента: в задачах должна остаться ссылка на ту же запись.
- Переведите задачу другому клиенту и проверьте обе обратные стороны.
- Отфильтруйте задачи по статусу, отсортируйте по сроку и верните полный список сбросом фильтров.
- Перезагрузите страницу и проверьте записи; затем выдайте тестовому участнику чтение и откройте карту под ним.

Не удаляйте столбец связи ради исправления одной ошибочной ячейки. Для большого набора используйте пагинацию; длинный обратный список раскрывается кнопкой **Ещё** порциями по 10.

[Настройка связей](maps-relations.md) · [Таблицы и формы](maps-entities.md) · [Доступ](maps-sharing.md)`),
				'maps-recipe-dashboard': page('Рецепт: отчёт с графиком', 'folder-map-recipes', `**Результат:** таблица продаж, связанный график, фильтр и пояснение на одной карте. Начните с готового пресета — он уже связывает источник и визуализацию.

## 1. Добавить и проверить данные

В меню карты выберите **ECharts**. Появятся график и таблица Tabulator с Датой, Продажами и Каналом. Дайте им понятные названия. Сначала измените одно число в примере и убедитесь, что график реагирует; затем замените пример собственными строками.

Используйте одинаковый формат дат и числовые значения продаж. Если строка не попала в график, проверьте тип и содержимое ячейки, а не только подпись столбца. Сохраните исходные данные отдельно.

## 2. Настроить отображение

Через ПКМ графика нажмите **Добавить виджет настроек**. Задайте Ось = Дата, Значения = Продажи, Группировка = Канал. Выберите Линию для динамики или Столбцы для сравнения. При необходимости включите легенду и масштабирование.

Сверьте несколько точек с таблицей. Для месяца или года измените детализацию даты и проверьте итог на маленьком известном наборе. Не делайте вывод о показателе по одному красивому графику без проверки чисел.

## 3. Добавить фильтр и пояснение

В ПКМ графика выберите **Добавить фильтр** по нужному полю, например Канал. Выберите одну категорию и проверьте, что изменились именно ожидаемые серии; затем сбросьте фильтр.

Добавьте Markdown-заметку: что измеряется, единицы, период, источник и дата обновления. Сложите пояснение и график в контейнер или viewport-рамку. При показе зрителям таблица-источник должна оставаться доступной для проверки, но её можно расположить отдельно от обзорного экрана.

## 4. Передать результат

Выдайте чтение проверяющему, откройте карту новым клиентом и пройдите фильтр. Проверьте узкое окно и полноэкранный режим графика. Экспортируйте карту перед дальнейшими экспериментами. Публичное редактирование не нужно, если аудитории достаточно просматривать отчёт.

[Параметры графиков](maps-charts.md) · [Режим просмотра](maps-presentation.md) · [Резервная копия](maps-transfer.md)`),
			}
		}
	}
}
